/* eslint-disable complexity */
import app from 'Cloud/Application/app';
import { path } from 'ramda';
import { downloadItem, downloadItemsZip, initUploadInput, openShareDialog, publishSocial } from 'reactApp/appHelpers/appHelpers';
import { IS_MOBILE_OR_TABLET_BROWSER, IS_REACT_PAGE } from 'reactApp/appHelpers/configHelpers';
import { renderRenameDialog } from 'reactApp/components/BaseConfirmDialog/BaseInputDialog.helpers';
import { renderEmptyTrashBinDialog } from 'reactApp/components/EmptyTrashBin/EmptyTrashBin.helpers';
import { ROOT_FOLDER_ID } from 'reactApp/constants/magicIdentificators';
import { createAndPublishAlbumRequest } from 'reactApp/modules/albums/albums.actions';
import { isCreatePublicAlbumsEnabled } from 'reactApp/modules/albums/albums.selector';
import { getSendViaMailruLink } from 'reactApp/modules/attaches/attaches.helpers';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { getFilesWithFaceIdxs, getFileWithFaceById, getSelectedFaceId } from 'reactApp/modules/faces/faces.selectors';
import {
    addToFavorites,
    cloneRequest,
    createFolderAction,
    downloadMobileItem as downloadMobileItemAction,
    mountRequest,
    removeFromFavorites,
    removeRequest,
} from 'reactApp/modules/modifying/modifying.actions';
import { getGoToAndScrollUrl } from 'reactApp/modules/modifying/modifying.helpers';
import { PublishItem } from 'reactApp/modules/modifying/modifying.types';
import { openPopupHelper } from 'reactApp/modules/popup/popup.helpers';
import { popupNames } from 'reactApp/modules/popup/popup.types';
import { PublicFile } from 'reactApp/modules/public/public.types';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { scrollToHomeItemAction, toggleAll } from 'reactApp/modules/selections/selections.actions';
import { SelectionsSelectors } from 'reactApp/modules/selections/selections.selectors';
import { settingsController } from 'reactApp/modules/settings/settings.controller';
import { showSnackbarAction } from 'reactApp/modules/snackbar/snackbar.actions';
import { snackbarController } from 'reactApp/modules/snackbar/snackbar.controller';
import { SnackbarTypes } from 'reactApp/modules/snackbar/snackbar.types';
import { setSort } from 'reactApp/modules/sort/sort.module';
import { ESortOder, ESortTypes } from 'reactApp/modules/sort/sort.types';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import {
    filterIdsByAuthors,
    getCurrentFolder as getCurrentFolderAction,
    getIds,
    getItemById,
    getStorageItemById,
    getStorageSelectedItems,
} from 'reactApp/modules/storage/storage.selectors';
import { CloudItem, EStorageType } from 'reactApp/modules/storage/storage.types';
import { trashbinRestoreAllRequest, trashbinRestoreRequest } from 'reactApp/modules/trashbin/trashbin.module';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { attachesController } from 'reactApp/sections/AttachesPage/attachesController';
import { sendQuotaCleanerGa } from 'reactApp/sections/QuotaLanding/QuotaLanding.helpers';
import { store as reduxStore } from 'reactApp/store';
import { Sort } from 'reactApp/types/Tree';
import { openDisabledFeaturePopupHelper } from 'reactApp/ui/DisabledFeatureDialog/DisabledFeatureDialog.helpers';
import { DisabledFeature } from 'reactApp/ui/DisabledFeatureDialog/DisabledFeatureDialog.types';
import { renderAuthModalDialog } from 'reactApp/ui/Mobile/AuthModal/AuthModal.helpers';
import { renderCopyDialog, renderCreateShareDialog, renderMoveDialog } from 'reactApp/ui/SelectFolderDialog/SelectFolderDialog.toolkit';
import { renderUploadDialog } from 'reactApp/ui/UploadDialog/UploadDialog.helpers';
import { copy as copyToClipboard } from 'reactApp/utils/copyToClipboard';
import opener from 'reactApp/utils/opener';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';
import { getEncodePath } from 'reactApp/utils/urlHelper';

import { publishHelper, unPublishHelper } from './publishHelper';

const getSelectedItems = (state) => {
    const selectedIdxs = SelectionsSelectors.getSelectedIdxs(state);
    const selectedFace = getSelectedFaceId(state);
    if (selectedFace && !selectedIdxs.length) {
        return getFilesWithFaceIdxs(state, selectedFace)
            .map((id) => getFileWithFaceById(state, id))
            .filter((item): item is PublicFile => !!item);
    }
    const selectedStorage = SelectionsSelectors.getStorageSelected(state);
    return selectedIdxs
        .map((id) => getStorageItemById(state, selectedStorage || getCurrentStorage(state), id))
        .filter((item): item is CloudItem => !!item);
};

const getCurrentFolder = (state) => {
    const folder = getCurrentFolderAction(state);

    return folder?.id && folder?.id !== ROOT_FOLDER_ID ? folder : null;
};

const getSelectedItemsOrCurrentFolder = (id?: string) => {
    const state = reduxStore.getState();
    let selectedItems = getSelectedItems(state);
    const item = id && getItemById(state, id);

    if (item) {
        return [item];
    }

    if (!Array.isArray(selectedItems) || selectedItems.length === 0) {
        const folder = getCurrentFolder(state);
        if (folder) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            selectedItems = [folder];
        }
    }

    return selectedItems;
};

const create = (type: string, from: string, showShareOnlyButton?: boolean, gaPrefix = ''): void => {
    const isFolder = type === 'folder';

    const state = reduxStore.getState();
    const currentStorage = getCurrentStorage(state);
    const { isSearch, isSharedLinks, isTrashBin, isHome, isStart } = getStorage(currentStorage);
    const folder = getCurrentFolderAction(state);
    const id = folder?.id || ROOT_FOLDER_ID;

    const { isFull } = UserSelectors.getCloudSpace(state);

    if (isFull) {
        openDisabledFeaturePopupHelper({ disabledFeature: isFolder ? DisabledFeature.newFolder : DisabledFeature.newFile });
        return;
    }

    let currentFolder = id;

    if (isHome) {
        currentFolder = getCurrentFolder(state)?.id || '/';
    } else if (IS_REACT_PAGE) {
        currentFolder = ROOT_FOLDER_ID;
    }

    const storage = isSearch || isSharedLinks || isTrashBin || isStart || isHome ? EStorageType.home : undefined;

    if (isFolder) {
        sendPaymentGa({
            eventCategory: ECategoryGa.created,
            action: 'created-folder',
            type_folder: showShareOnlyButton ? 'common-folder' : 'folder',
            source: from,
        });

        reduxStore.dispatch(
            createFolderAction({
                parentFolderId: currentFolder,
                storage,
                showCreateButton: !showShareOnlyButton,
                gaPrefix,
            })
        );
        return;
    }

    sendPaymentGa({
        eventCategory: ECategoryGa.created,
        action: 'created-files',
        type_file: type,
        source: from,
    });

    app.editor.createNew(type, null, currentFolder);
};

const view = (type: string) => {
    const state = reduxStore.getState();
    const currentStorage = getCurrentStorage(state);

    sendPaymentGa({
        eventCategory: ECategoryGa.toolbar,
        action: 'change_view',
        source: currentStorage,
    });

    settingsController.setViewMode(type);
};

const sort = (name: string) => {
    const sortName = name.split('-');
    const sort: Sort = { type: sortName[0] as ESortTypes, order: sortName[1] as ESortOder };
    const state = reduxStore.getState();
    const folder = getCurrentFolderAction(state);

    reduxStore.dispatch(setSort({ sort, id: folder?.id || ROOT_FOLDER_ID }));
};

const rename = (id?: string): void => {
    const selectedItems = getSelectedItemsOrCurrentFolder(id);

    if (selectedItems?.length !== 1 || !selectedItems?.[0]) {
        return;
    }

    renderRenameDialog(selectedItems[0]);
};

const toggleFavorites = (): void => {
    const state = reduxStore.getState();
    const selectedItems = getSelectedItems(state);

    if (!selectedItems?.length) {
        return;
    }

    const isAllInFav = selectedItems.every((item) => path(['isInFavorites'], item));

    if (isAllInFav) {
        reduxStore.dispatch(removeFromFavorites({ items: selectedItems }));
        return;
    }

    reduxStore.dispatch(addToFavorites({ items: selectedItems, from: 'toolbar' }));
};

const copy = (id?: string, sendAnalytics?: (data?: any) => void): void => {
    const state = reduxStore.getState();
    const currentStorage = getCurrentStorage(state);
    const items = getSelectedItemsOrCurrentFolder(id);

    if (!items?.length) {
        return;
    }

    const onSuccess = () => {
        sendAnalytics?.({
            action: 'copy',
            source: currentStorage,
            count_files: items.length,
        });
    };

    renderCopyDialog({ items, onSuccess });
};

const download = ({ useDownloadUrl = false, id }: { useDownloadUrl?: boolean; id?: string } = {}): void => {
    const state = reduxStore.getState();
    const currentFolder = getCurrentFolderAction(state);
    let selected = getSelectedItems(state);
    const currentStorage = getCurrentStorage(state);
    const item = id && getItemById(state, id);

    if (item && !selected.length) {
        selected = [item];
    }

    const { isAttaches, isFavorites, isSearch, isHome, isPublic } = getStorage(currentStorage);

    if (!currentFolder && !selected?.length) {
        return;
    }

    sendPaymentGa({
        eventCategory: ECategoryGa.toolbar,
        action: 'download_content',
        source: currentStorage,
        count_files: selected.length || 1,
    });

    if (isAttaches && selected.length) {
        attachesController.downloadAttaches(selected);
        return;
    }

    if ((isFavorites || isSearch) && !selected.length && currentFolder && 'list' in currentFolder) {
        downloadItemsZip({ items: Object.values(currentFolder.list), name: isFavorites ? 'favorites' : 'archive' });
        return;
    }

    if (selected.length === 1) {
        if (selected[0].isFolder) {
            downloadItemsZip({ items: selected, name: selected[0].name });
        } else {
            downloadItem({ itemOrId: selected[0], useDownloadUrl, storage: currentStorage });
        }

        return;
    }

    const name = (isHome || isPublic) && currentFolder && 'name' in currentFolder ? currentFolder?.name : 'archive';
    downloadItemsZip({ items: selected.length ? selected : [currentFolder], name });
};

const viewHistory = (): void => {
    const state = reduxStore.getState();
    const selectedItem = getSelectedItems(state)[0];
    openPopupHelper({ popupName: popupNames.FILE_HISTORY, data: { itemId: selectedItem?.id } });
};

const move = (id?: string, sendAnalytics?: (data?: any) => void): void => {
    const state = reduxStore.getState();
    const currentStorage = getCurrentStorage(state);
    const items = getSelectedItemsOrCurrentFolder(id);

    if (!items.length) {
        return;
    }

    const onSuccess = () => {
        sendAnalytics?.({
            action: 'move_to_folder',
            source: currentStorage,
            count_files: items.length,
        });
    };

    renderMoveDialog({ items, onSuccess });
};

const remove = (id?: string, sendAnalytics?: (data?: any) => void): void => {
    const state = reduxStore.getState();
    const currentStorage = getCurrentStorage(state);
    const items = getSelectedItemsOrCurrentFolder();
    const item = id && getItemById(state, id);

    const onSuccess = () => {
        sendAnalytics?.({
            action: 'delete',
            source: currentStorage,
            count_files: items.length,
        });
    };

    reduxStore.dispatch(removeRequest({ items: item ? [item] : items, withRemoveDialog: true, onSuccess }));
};

const upload = (from?: string): void => {
    const state = reduxStore.getState();

    const folder = getCurrentFolderAction(state);

    if (!folder) {
        return;
    }

    const isOverquotaUser = UserSelectors.isOverQuotaUser(state);

    if (isOverquotaUser) {
        snackbarController.showSnackbar({
            id: 'overquota',
            text: 'Превышен объём Облака',
            type: SnackbarTypes.failure,
            closable: true,
        });

        return;
    }

    renderUploadDialog({
        folderId: folder.id,
        folderHome: 'home' in folder && folder.home,
        folderName: 'name' in folder && folder.name,
        setInputs: initUploadInput,
        openFrom: from,
    });
};

const selectAll = (): void => {
    const state = reduxStore.getState();
    const storage = getCurrentStorage(state);

    const ids = getIds(state, storage);
    const allIdxs = filterIdsByAuthors(state, ids, storage);
    const allSelectedIdx = getStorageSelectedItems(state, storage);
    const isDeselectAll = allIdxs?.length && allIdxs?.length === allSelectedIdx?.length;

    reduxStore.dispatch(toggleAll({ allIdxs, storage }));

    if (storage === EStorageType.quotaCleaner) {
        const prefix = isDeselectAll ? 'all-files-select-cancel' : 'all-files-select';
        sendQuotaCleanerGa({ action: prefix, dwh: { count_select_files: allIdxs?.length } });
    }

    if (!isDeselectAll) {
        sendPaymentGa({
            eventCategory: ECategoryGa.toolbar,
            action: 'select_all',
            source: storage,
        });
    }
};

const share = (id?: string): void => {
    const state = reduxStore.getState();
    const selectedItems = getSelectedItemsOrCurrentFolder();
    const item = (id && getItemById(state, id)) || (selectedItems.length === 1 && selectedItems[0]);

    if ((item && item.id === '/') || !selectedItems.length) {
        renderCreateShareDialog({
            onSuccess: (item) => reduxStore.dispatch(openShareDialog({ item })),
        });
        return;
    }

    reduxStore.dispatch(openShareDialog({ item }));
};

const publish = (publishFrom = '', id?: string): void => {
    const state = reduxStore.getState();
    const selectedItems = getSelectedItemsOrCurrentFolder(id);
    const item = selectedItems.length === 1 ? selectedItems[0] : undefined;
    const isPublicAlbumEnabled = isCreatePublicAlbumsEnabled(state, selectedItems);

    if (!selectedItems.length || item?.id === '/') {
        return;
    }

    if (isPublicAlbumEnabled) {
        reduxStore.dispatch(createAndPublishAlbumRequest({ items: selectedItems }));
        return;
    }

    publishHelper({ item, publishFrom });
};

const unPublish = (): void => {
    const state = reduxStore.getState();
    const selectedItems = getSelectedItems(state).filter((item) => path(['weblink'], item)) as PublishItem[];

    if (!selectedItems.length) {
        return;
    }

    unPublishHelper({ items: selectedItems });
};

const restore = (id?: string): void => {
    const state = reduxStore.getState();
    const ids = id ? [id] : SelectionsSelectors.getSelectedIdxs(state);
    reduxStore.dispatch(trashbinRestoreRequest({ ids, from: 'toolbar' }));
};

const restoreAll = (): void => {
    reduxStore.dispatch(trashbinRestoreAllRequest());
};

const clear = (): void => {
    renderEmptyTrashBinDialog();
};

const forward = ({ items }: { items: CloudItem[] | null } = { items: null }): void => {
    const state = reduxStore.getState();
    const storage = SelectionsSelectors.getStorageSelected(state) || getCurrentStorage(state);

    if (storage === EStorageType.attaches) {
        attachesController.forwardSelectedAttaches();
    } else {
        if (!items) {
            items = getSelectedItems(state);
        }

        if ((storage === EStorageType.public || storage === EStorageType.stock) && (!items || items.length <= 1)) {
            const item = items.length ? items[0] : getCurrentFolderAction(state);
            publishSocial('email', item);
            return;
        }

        if (Array.isArray(items)) {
            const mailLink = getSendViaMailruLink(items, storage);
            window.open(mailLink, '_blank');
        }
    }
};

const mount = (): void => {
    const state = reduxStore.getState();
    const selectedItems = getSelectedItems(state);
    const item = selectedItems.length === 1 && selectedItems[0];

    if (!item) {
        return;
    }

    // @ts-ignore
    reduxStore.dispatch(mountRequest({ item }));
};

interface IToolbarClone {
    // source для dwh
    source: string;
    id?: string;
    destination?: string;
    autoClone?: boolean;
}

const clone = ({ id, source, destination, autoClone }: IToolbarClone): void => {
    const state = reduxStore.getState();
    const items = getSelectedItemsOrCurrentFolder();
    const item = id && getItemById(state, id);
    const isAnonymous = UserSelectors.isAnonymous(state);
    const isPhone = EnvironmentSelectors.isPhone();

    if (isAnonymous && isPhone) {
        renderAuthModalDialog();
        return;
    }

    reduxStore.dispatch(cloneRequest({ items: item ? [item] : items, source, destination, autoClone }))
};

const shareLink = ({ id, url, useNativeShare = true }: { id?: string; url?: string; useNativeShare?: boolean }): void => {
    const state = reduxStore.getState();
    const storage = getCurrentStorage(state);

    const isNativeShareSupported = IS_MOBILE_OR_TABLET_BROWSER && 'share' in navigator && typeof navigator.share === 'function';

    const href = id ? `${window.location.origin}/${storage}/${id}` : window.location.href;
    const copyUrl = url || href;

    if (isNativeShareSupported && useNativeShare) {
        navigator.share({ url: copyUrl }).catch(() => {});
        return;
    }

    copyToClipboard(copyUrl)
        .then(({ hasBeenCopied }) => {
            if (hasBeenCopied) {
                reduxStore.dispatch(
                    showSnackbarAction({
                        id: 'copy-link',
                        closable: true,
                        text: 'Ссылка успешно скопирована',
                        type: SnackbarTypes.success,
                    })
                );
            }
        })
        .catch(() => {
            reduxStore.dispatch(
                showSnackbarAction({
                    id: 'copy-link',
                    closable: true,
                    text: 'Не удалось скопировать ссылку',
                    type: SnackbarTypes.failure,
                })
            );
        });
};

const downloadMobileItem = () => {
    reduxStore.dispatch(downloadMobileItemAction());
};

const showInParentFolder = ({ folderId, itemId }: { folderId: string; itemId?: string }) => {
    const state = reduxStore.getState();
    const storage = getCurrentStorage(state);

    const { isReactPage } = getStorage(storage);

    if (!isReactPage) {
        const selectedFolderId = folderId === ROOT_FOLDER_ID ? '' : getEncodePath(folderId);
        const url = getGoToAndScrollUrl(selectedFolderId, itemId);

        opener(url, true);
        return;
    }

    reduxStore.dispatch(scrollToHomeItemAction({ parentFolderId: folderId, itemId }));
};

export const toolbarActions = {
    create,
    view,
    sort,
    rename,
    toggleFavorites,
    copy,
    download,
    viewHistory,
    move,
    remove,
    upload,
    selectAll,
    share,
    publish,
    unPublish,
    restore,
    restoreAll,
    clear,
    forward,
    mount,
    clone,
    shareLink,
    downloadMobileItem,
    showInParentFolder,
};
